const LOCK_PREFIX = 'avatar-stream-lock:';
const TAB_ID =
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random()}`;

type LockRecord = { tabId: string; at: number };

function lockStorageKey(institutionId: string): string {
  return `${LOCK_PREFIX}${institutionId}`;
}

function readLock(institutionId: string): LockRecord | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(lockStorageKey(institutionId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as LockRecord;
    if (typeof parsed.tabId !== 'string') return null;
    return parsed;
  } catch {
    return null;
  }
}

export type AvatarTabLock = {
  acquire: () => void;
  release: () => void;
  isHeldByOtherTab: () => boolean;
  tabId: string;
};

export function createAvatarTabLock(institutionId: string): AvatarTabLock {
  const storageKey = lockStorageKey(institutionId);
  let channel: BroadcastChannel | null = null;

  if (typeof BroadcastChannel !== 'undefined') {
    channel = new BroadcastChannel(storageKey);
  }

  const acquire = () => {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(storageKey, JSON.stringify({ tabId: TAB_ID, at: Date.now() } satisfies LockRecord));
    channel?.postMessage({ type: 'acquired', tabId: TAB_ID });
  };

  const release = () => {
    if (typeof localStorage === 'undefined') return;
    const lock = readLock(institutionId);
    if (lock?.tabId === TAB_ID) {
      localStorage.removeItem(storageKey);
      channel?.postMessage({ type: 'released', tabId: TAB_ID });
    }
  };

  const isHeldByOtherTab = () => {
    const lock = readLock(institutionId);
    return lock != null && lock.tabId !== TAB_ID;
  };

  return { acquire, release, isHeldByOtherTab, tabId: TAB_ID };
}
